import Menu, { type MenuItem } from "@/components/Menu";
import Icon from "@/components/Icon";
import {
  COMPACT_GLOBAL_NAVBAR_ICON_SIZE_PX,
  compactGlobalToolbarControlClassName,
  globalToolbarBorderlessTriggerClassName
} from "./compactNavbarChrome";
import { DOMAIN } from "@/hooks/useCapabilities";
import type { PrimaryNavTabItem } from "@/layouts/AppLayout/primaryNavTabItems";
import { getGoNavChordKey } from "@/layouts/AppLayout/primaryNavTabItems";
import { type Path, useNavigate } from "@/router";
import classNames from "classnames";

export type CompactPrimaryNavMenuProps = {
  tabItems: PrimaryNavTabItem[];
  domains: DOMAIN[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Show key-cap chord hints beside primary-nav menu items (go-nav mode). */
  showChordKeyCaps?: boolean;
};

const COMPACT_PRIMARY_NAV_MENU_TITLE = "Switch to";

function primaryNavItemSlug(label: string): string {
  return label.toLowerCase().replace(/\s+/g, "-");
}

export default function CompactPrimaryNavMenu({
  tabItems,
  domains,
  open,
  onOpenChange,
  showChordKeyCaps = false
}: CompactPrimaryNavMenuProps) {
  const navigate = useNavigate();

  const permittedLinks = tabItems.filter(
    (navLink) => navLink.domain === DOMAIN.EVERYONE || domains.includes(navLink.domain)
  );

  const menuItems: MenuItem[] = permittedLinks.map((link) => {
    const slug = primaryNavItemSlug(link.label);
    return {
      label: link.label,
      iconName: link.icon,
      onClick: () => navigate(link.link.to as Path),
      dataTestId: showChordKeyCaps ? `go-nav-${slug}` : `compact-primary-nav-${slug}`,
      chordKey: showChordKeyCaps ? getGoNavChordKey(link.label) : undefined,
      useDivider: false,
      itemClassName: classNames("min-w-[215px]", {
        "text-sys-brand-primary": link.active
      })
    };
  });

  if (permittedLinks.length === 0) {
    return null;
  }

  const activeLink = permittedLinks.find((link) => link.active);
  const triggerLabel = activeLink?.label ?? "Menu";
  const triggerIcon = activeLink?.icon ?? "menu";

  const trigger = (
    <button
      type="button"
      data-testid="compact-primary-nav-trigger"
      className={classNames(
        compactGlobalToolbarControlClassName,
        globalToolbarBorderlessTriggerClassName,
        "flex min-w-0 max-w-[9.5rem] items-center gap-1 px-1"
      )}
      aria-haspopup="menu"
      aria-label={`Current destination: ${triggerLabel}. Open primary navigation.`}
    >
      <Icon
        name={triggerIcon}
        size={COMPACT_GLOBAL_NAVBAR_ICON_SIZE_PX}
        weight={800}
        className="mr-1.5 shrink-0 text-sys-brand-on-surface"
      />
      <span className="min-w-0 flex-1 truncate text-body-medium leading-none text-sys-brand-on-surface">
        {triggerLabel}
      </span>
      <Icon
        name="expand_more"
        size={COMPACT_GLOBAL_NAVBAR_ICON_SIZE_PX}
        weight={800}
        className="shrink-0 text-sys-brand-on-surface"
      />
    </button>
  );

  return (
    <Menu
      menuItems={menuItems}
      title={COMPACT_PRIMARY_NAV_MENU_TITLE}
      AnchorComponent={trigger}
      yOffset={8}
      open={open}
      onOpenChange={onOpenChange}
      showChordKeyCaps={showChordKeyCaps}
      arrowNavigationAxis="horizontal"
    />
  );
}
